<script setup>
/**
 * What WhatsApp shows for one approved message (spec P-4.7): the header, the body with its
 * blanks filled, the footer, and the buttons.
 *
 * ── WHY A SECOND RENDERER, AFTER ALL ────────────────────────────────────────────────
 *
 * The editor used Chatwoot's `TemplatePreview` so that two mocks could not drift. It drifted
 * anyway, in the one direction that matters: for a template with buttons it picks
 * `CallToActionTemplate`, which draws the body and the buttons and drops the header and the
 * footer, and nothing in it sets a direction, so an Arabic message read left to right. An owner
 * checking her words saw a message that was not the one WhatsApp would send.
 *
 * This one draws every part, in Meta's order, from the same Meta-shaped object `draftToPreview`
 * already builds. The bubble is right to left for an Arabic template and every text block is
 * `dir="auto"`, so a Latin brand name inside Arabic keeps its own direction. With more than
 * three buttons it shows two and "See all options", which is what WhatsApp itself does. Chatwoot's
 * component is not edited and still renders templates everywhere else.
 */
import { computed } from 'vue';

const props = defineProps({
  /** Meta's template shape: `{ components: [{ type, format?, text?, buttons? }] }`. */
  template: { type: Object, required: true },
  /** `{ [blank]: value }` from `previewVariables`. A blank with no value shows as `[blank]`. */
  variables: { type: Object, default: () => ({}) },
  /** The template's language (`ar`, `en_US`, …). */
  lang: { type: String, default: '' },
  /** Translated words the object does not carry: `{ mediaHeader, seeAll }`. */
  labels: { type: Object, default: () => ({}) },
});

/** WhatsApp's own rule: above three, two show and the rest sit behind "See all options". */
const SHOWN_WITHOUT_TAP = 3;

const ICON = {
  QUICK_REPLY: 'i-lucide-reply',
  URL: 'i-lucide-external-link',
  PHONE_NUMBER: 'i-lucide-phone',
  COPY_CODE: 'i-lucide-copy',
  OTP: 'i-lucide-copy',
};

const BLANK = /\{\{\s*([^}]*?)\s*\}\}/g;

const partOf = type =>
  (Array.isArray(props.template?.components)
    ? props.template.components
    : []
  ).find(part => String(part?.type ?? '').toUpperCase() === type) ?? null;

const fill = value =>
  String(value ?? '').replace(BLANK, (match, name) => {
    const given = props.variables?.[name];
    return typeof given === 'string' && given !== '' ? given : `[${name}]`;
  });

const header = computed(() => {
  const part = partOf('HEADER');
  if (!part) return null;
  const format = String(part.format ?? 'TEXT').toUpperCase();
  return format === 'TEXT' ? { text: fill(part.text) } : { media: format };
});

const body = computed(() => fill(partOf('BODY')?.text));
const footer = computed(() => String(partOf('FOOTER')?.text ?? ''));

const buttons = computed(() =>
  (Array.isArray(partOf('BUTTONS')?.buttons)
    ? partOf('BUTTONS').buttons
    : []
  ).map((button, index) => {
    const type = String(button?.type ?? '').toUpperCase();
    return {
      key: index,
      type,
      text: String(button?.text ?? ''),
      icon: ICON[type] ?? 'i-lucide-square',
    };
  })
);

const tooMany = computed(() => buttons.value.length > SHOWN_WITHOUT_TAP);
const shownButtons = computed(() =>
  tooMany.value ? buttons.value.slice(0, 2) : buttons.value
);

const rtl = computed(() =>
  String(props.lang ?? '')
    .toLowerCase()
    .startsWith('ar')
);
</script>

<template>
  <div
    class="flex flex-col w-full max-w-[20rem] gap-1"
    data-waslo-template-preview-card
  >
    <div
      class="flex flex-col gap-2 px-3 py-2 rounded-lg shadow-sm bg-n-solid-1 text-n-slate-12"
      :dir="rtl ? 'rtl' : undefined"
      data-waslo-preview-bubble
    >
      <div
        v-if="header && header.media"
        class="grid h-24 px-3 text-xs text-center rounded-md place-items-center bg-n-alpha-2 text-n-slate-11"
        data-waslo-preview-media
      >
        {{ labels.mediaHeader ?? '' }}
      </div>
      <p
        v-else-if="header"
        class="mb-0 text-sm font-semibold break-words"
        dir="auto"
        data-waslo-preview-header
      >
        {{ header.text }}
      </p>
      <p
        class="mb-0 text-sm break-words whitespace-pre-wrap"
        dir="auto"
        data-waslo-preview-body
      >
        {{ body }}
      </p>
      <p
        v-if="footer"
        class="mb-0 text-xs break-words text-n-slate-10"
        dir="auto"
        data-waslo-preview-footer
      >
        {{ footer }}
      </p>
    </div>
    <div
      v-for="button in shownButtons"
      :key="button.key"
      class="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-n-solid-1 text-n-blue-11"
      :data-waslo-preview-button-type="button.type"
      data-waslo-preview-button
    >
      <span class="size-4 shrink-0" :class="button.icon" aria-hidden="true" />
      <span dir="auto">{{ button.text }}</span>
    </div>
    <div
      v-if="tooMany"
      class="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-n-solid-1 text-n-blue-11"
      data-waslo-preview-see-all
    >
      <span class="size-4 shrink-0 i-lucide-list" aria-hidden="true" />
      <span>{{ labels.seeAll ?? '' }}</span>
    </div>
  </div>
</template>
